import {useMutation} from '@tanstack/react-query'
import {useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import useRequestParams from 'src/feature/bite/hook/request'

export const createCampaignProduct = async (
  userID: string,
  headers: any,
  campaignID: string,
  productIDList: string[]
) => {
  if (productIDList.length === 0) {
    return
  }

  const reqBodyProduct = {
    language: headers.Language,
    userID: userID,
    campaignID,
    productIDList,
  }

  const apiUrl = process.env.REACT_APP_CAMPAIGN_URL
  const ResponseProduct = await fetch(`${apiUrl}/CampaignProduct/CreateCampaignProduct`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyProduct),
  })

  const DataProduct = await ResponseProduct.json()

  return DataProduct
}

export function useBecomeAMerchant() {
  return useMutation(
    ['BecomeAMerchant'],
    async ({values}: {values: any}) => {
      const Response = await fetch(`${API}/Merchant/BecomeAMerchant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      })

      const Data = await Response.json()

      if (!Response.ok) {
        throw new Error(Data.message)
      }

      return true
    },
    {
      onSuccess: () => {
        toast.success('Congratulation! You are now a merchant', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
      onError: (error) => {
        console.log(error)
      },
    }
  )
}

export function useContactForm() {
  const intl = useIntl()

  return useMutation(
    ['ContactForm'],
    async ({values, recaptcha}: {values: any; recaptcha: string}) => {
      if (values.recaptcha === '' || !values.recaptcha || values.recaptcha === recaptcha) {
        toast.error(intl.formatMessage({id: 'CAPTCHA.ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      const Response = await fetch(`${API}/Merchant/CreateContactUsForm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      })

      const Data = await Response.json()

      if (!Response.ok) {
        toast.error(Data.responseResult.message, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!Data.responseResult.result) {
        toast.error(Data.responseResult.exceptionDetail, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      return true
    }
  )
}

export function useCreateCampaign() {
  const {headers, errors} = useRequestParams()
  const router = useNavigate()

  return useMutation(
    ['CampaignCreate'],
    async ({
      values,
      products,
      uploadFileIdsState,
      vipTierIDList,
      isQuickStart = false,
      userID,
      token,
    }: {
      values: any
      products: string[]
      uploadFileIdsState: any
      vipTierIDList?: string[]
      isQuickStart?: boolean
      userID: string
      token?: any
    }) => {
      if (!userID) {
        Error('User not found')
        return
      }

      if (values?.selectedEarningType === 1) {
        values.campaignPoint = {
          ...values.campaignPoint,
          pointsToBeEarned: 0,
          amountToBeSpent: 0,
        }
      }

      if (values?.selectedEarningType === 0) values.campaignPriceClusterList = []

      if (values?.productInSelectionType === '2' && products?.length === 0)
        values.productInSelectionType = '1'

      // eslint-disable-next-line eqeqeq
      if (
        values.campaignData.promoCluster == 1 &&
        values.campaignPriceClusterList?.filter((item: any) => !item?.isDeleted)?.length === 0
      ) {
        Error('Please add a price cluster')
        return
      }

      // eslint-disable-next-line eqeqeq
      if (
        values.campaignData.promoCluster == 2 &&
        values.campaignData.promoGroup == 11 &&
        values.selectedEarningType == 0 &&
        (values.campaignPoint.pointsToBeEarned == 0 || values.campaignPoint.amountToBeSpent == 0)
      ) {
        Error('Please enter the point values')
        return
      }

      if (
        values.campaignData.promoCluster == 2 &&
        values.campaignData.promoGroup == 11 &&
        values.selectedEarningType == 1 &&
        values.campaignPriceClusterList.length === 0
      ) {
        Error('Please add a price cluster')
        return
      }

      if (token && token?.token) {
        headers.Authorization = `Bearer ${token.token}`
      }

      const apiUrl = process.env.REACT_APP_CAMPAIGN_URL

      const Response = await fetch(`${apiUrl}/Campaign/CreateCampaign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          language: headers.Language,
          campaign: {
            timeZoneOffset: getTimeZone(),
            ...values.campaignData,
          },
          userID: userID,
        }),
      })

      const Data = await Response.json()
      if (!Response.ok || !Data.result.isSuccess) {
        Error(Data?.result?.errorMessage || errors.campaign.create_error)

        return false
      }

      if (!Data.campaignID) return

      // BEGIN: CLUSTER CREATE

      if (
        values.campaignPriceClusterList &&
        values.campaignPriceClusterList?.filter((item: any) => !item?.isDeleted)?.length > 0
      ) {
        const apiUrl = process.env.REACT_APP_CAMPAIGN_URL

        const priceResponse = await fetch(
          `${apiUrl}/CampaignPriceCluster/CreateCampaignPriceCluster`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              language: headers.Language,
              campaignID: Data.campaignID,
              campaignPriceClusterList: values.campaignPriceClusterList?.filter(
                (item: any) => !item?.isDeleted
              ),
              userID: userID,
            }),
          }
        )

        const priceData = await priceResponse.json()

        if (!priceResponse.ok || !priceData.result.isSuccess) {
          Error(priceData?.result?.errorMessage || errors.product.create_error)
        }
      }

      if (values.productInSelectionType === '1') {
        await createCampaignProduct(userID, headers, Data.campaignID, ['All'])
      }

      if (values.productInSelectionType === '2') {
        await createCampaignProduct(userID, headers, Data.campaignID, products)
      }

      // Discount mı yoksa Point mi seçildiğine göre işlem yapılıyor
      /*  if (values.campaignData.promoCluster === 1) {
        router(`/kick-start/success`)

        return true
      } */

      const pointResponse = await fetch(`${apiUrl}/CampaignPoint/CreateCampaignPoint`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          language: headers.Language,
          campaignID: Data.campaignID,
          campaignPoint: {
            ...values.campaignPoint,
          },
          userID: userID,
        }),
      })

      const pointData = await pointResponse.json()

      if (!pointResponse.ok || !pointData.result.isSuccess) {
        Error(pointData?.result?.errorMessage || errors.campaign.create_error)

        return
      }

      //router(`/kick-start/success`)

      return true
    }
  )
}

export function useLaunchProgram() {
  const {headers} = useRequestParams()

  return useMutation(
    ['LaunchProgram'],
    async ({userID, merchantID, token}: {userID: string; merchantID: string; token?: any}) => {
      if (!userID) {
        Error('User not found')
        return
      }

      if (token && token?.token) {
        headers.Authorization = `Bearer ${token.token}`
      }

      const apiUrl = process.env.REACT_APP_API_URL

      await fetch(`${apiUrl}/Merchant/BecomeAMerchantLaunchProgram`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          merchantID,
        }),
      })

      return true
    }
  )
}
